import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useField } from 'formik';

import GenericShippingMethod from './GenericShippingMethod';
import ShippingAddresses from '../../ShippingAddresses/ShippingAddresses';
import Trans from 'components/Common/Translation/Trans';
import { MethodProps } from 'types/ShippingFormType';
import { formatPrice } from 'services/formatter';

import icon from 'assets/img/DeliveryBrand/picto_colissimo.svg';

import styles from './ShippingMethod.module.scss';

export default function Colissimo({
  isSelected,
  method: { methodCode, estimateDeliveryDates },
  cart,
  setShippingInformation,
}: MethodProps): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const [{ value: shippingSelected }] = useField('shippingMethod');
  const [{ value: addressSelected }, , { setValue: setAddressType }] = useField(
    'addressType'
  );

  const baseAmount = 3.5;

  const freeAMMinPrice = useMemo(() => {
    const freeAM = {
      G000: 0,
      G200: 30,
    };

    return cart.amListing.length > 0
      ? Object.entries(freeAM).find(([key]) =>
          cart.amListing.includes(key)
        )?.[1]
      : undefined;
  }, [cart.amListing]);

  const price = useMemo(() => {
    let basePrice = cart.shippingFees + baseAmount;
    if (undefined !== freeAMMinPrice && cart.subTotalPrice >= freeAMMinPrice) {
      basePrice -= baseAmount;
    }

    return basePrice;
  }, [cart.shippingFees, cart.subTotalPrice, freeAMMinPrice, baseAmount]);

  useEffect(() => {
    if (shippingSelected === methodCode) {
      const addressType = addressSelected || 'P';
      if (!addressSelected) {
        setAddressType(addressType, false);
      } else {
        setShippingInformation({
          shippingMethod: methodCode,
          addressType: addressSelected,
        });
      }
    }
  }, [methodCode, shippingSelected, addressSelected]);

  const freeFromParam = useCallback((): number | JSX.Element | undefined => {
    if (
      freeAMMinPrice !== undefined &&
      cart.freeShippingThresholdInformation?.amount
    ) {
      return cart.freeShippingThresholdInformation.amount;
    }

    if (cart.shippingFees && cart.freeShippingThresholdInformation?.amount) {
      return (
        <Trans
          i18nKey={'SHIPPING_COLISSIMO_METHOD_REDUCTION_FROM'}
          namespace={'checkout'}
          values={{
            reductionPrice: formatPrice(baseAmount),
            price: formatPrice(
              cart.freeShippingThresholdInformation.amount,
              true
            ),
          }}
        />
      );
    }

    return undefined;
  }, [freeAMMinPrice, cart.freeShippingThresholdInformation]);

  return (
    <GenericShippingMethod
      value={methodCode}
      methodIcon={<img src={icon} alt={'Colissimo icon'} />}
      methodLabel={
        <>
          <span className={styles.methodName}>
            {t('SHIPPING_COLISSIMO_METHOD_NAME')}
          </span>
          <span className={styles.methodNameExplain}>
            {t('SHIPPING_COLISSIMO_METHOD_NAME_EXPLAIN')}
          </span>
        </>
      }
      estimateDeliveryDates={estimateDeliveryDates}
      price={price}
      freeFrom={freeFromParam()}
      classes={{
        methodContent: classNames(styles.colissimoContent, {
          [styles.isSelected]: isSelected,
        }),
      }}
    >
      {isSelected && <ShippingAddresses fieldName={'addressType'} />}
    </GenericShippingMethod>
  );
}
